import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const StoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    brandId: "",
    address: "",
    phone: "",
    status: "ACTIVE",
  });

  useEffect(() => {
    fetchStores();
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/system/stores");
      setStores(response.data || []);
    } catch (error) {
      console.error("店舗一覧の取得に失敗しました:", error);
      setError("店舗一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/api/system/brands");
      setBrands(response.data || []);
    } catch (error) {
      console.error("ブランド一覧の取得に失敗しました:", error);
    }
  };

  const handleOpenDialog = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        name: store.name,
        brandId: store.brandId || "",
        address: store.address || "",
        phone: store.phone || "",
        status: store.status || "ACTIVE",
      });
    } else {
      setEditingStore(null);
      setFormData({
        name: "",
        brandId: "",
        address: "",
        phone: "",
        status: "ACTIVE",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStore(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (editingStore) {
        await api.put(`/api/stores/${editingStore.id}`, formData);
      } else {
        await api.post("/api/stores", formData);
      }
      fetchStores();
      handleCloseDialog();
    } catch (error) {
      console.error("店舗情報の保存に失敗しました:", error);
      setError("店舗情報の保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (storeId) => {
    if (window.confirm("この店舗を削除してもよろしいですか？")) {
      try {
        setDeletingId(storeId);
        await api.delete(`/api/stores/${storeId}`);
        fetchStores();
      } catch (error) {
        console.error("店舗の削除に失敗しました:", error);
        setError("店舗の削除に失敗しました");
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "ACTIVE":
        return "営業中";
      case "INACTIVE":
        return "休業中";
      default:
        return status;
    }
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          店舗管理
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          新規作成
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>店舗名</TableCell>
              <TableCell>ブランド</TableCell>
              <TableCell>運営会社</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (
              stores.map((store) => (
                <TableRow key={store.id}>
                  <TableCell>{store.name}</TableCell>
                  <TableCell>{store.brandName}</TableCell>
                  <TableCell>{store.companyName}</TableCell>
                  <TableCell>{store.address}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(store.status)}
                      color={getStatusColor(store.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(store)}
                      disabled={Boolean(deletingId)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(store.id)}
                      disabled={deletingId === store.id}
                    >
                      {deletingId === store.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStore ? "店舗情報編集" : "新規店舗作成"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="店舗名"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>ブランド</InputLabel>
                <Select
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleInputChange}
                  label="ブランド"
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address"
                label="住所"
                value={formData.address}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phone"
                label="電話番号"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>ステータス</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="ステータス"
                >
                  <MenuItem value="ACTIVE">営業中</MenuItem>
                  <MenuItem value="INACTIVE">休業中</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : "保存"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreManagement;
